import { useCallback, useEffect, useMemo, useState } from 'react';
import qrcode from 'qrcode-generator';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Notice,
  Panel,
  Select,
  Spinner,
  Table,
  TableWrap,
  Tabs,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { api, type Category, type Location } from '../api';

/**
 * Getting the register out of the register, and checking it against reality.
 *
 * Three things that only make sense next to each other: labels to stick on,
 * a walk round to see whether what is written down is true, and a file to send
 * to somebody who does not have a login.
 */
export function Reports() {
  const [tab, setTab] = useState('labels');

  return (
    <>
      <Toolbar>
        <Tabs
          items={[
            { id: 'labels', label: 'Labels' },
            { id: 'stocktake', label: 'Stock-take' },
            { id: 'export', label: 'Export' },
          ]}
          value={tab}
          onChange={setTab}
          label="Reports"
        />
      </Toolbar>

      {tab === 'labels' && <Labels />}
      {tab === 'stocktake' && <StockTake />}
      {tab === 'export' && <Export />}
    </>
  );
}

/* ------------------------------------------------------------- labels -- */

type Taggable = {
  id: string;
  name: string;
  assetTag: string | null;
  serialNumber: string | null;
  categoryName: string;
};

/**
 * A QR code as an SVG path.
 *
 * SVG rather than the library's own table or image output because these go to
 * a printer: a bitmap scaled to a 40mm label is a bitmap that will not scan,
 * and every module here is a crisp rectangle at any size.
 *
 * Error correction M, which tolerates about 15% damage. A label on a laptop
 * lives its life being rubbed by desks and bags, and the level above it costs
 * density this has no need to spend.
 */
function QrSvg({ text, size = 84 }: { text: string; size?: number }) {
  const path = useMemo(() => {
    // Type 0 means "work out the smallest version that fits".
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();

    const count = qr.getModuleCount();
    const parts: string[] = [];
    for (let row = 0; row < count; row += 1) {
      for (let col = 0; col < count; col += 1) {
        if (qr.isDark(row, col)) parts.push(`M${col} ${row}h1v1h-1z`);
      }
    }
    return { d: parts.join(''), count };
  }, [text]);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${path.count} ${path.count}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`QR code for ${text}`}
    >
      <rect width={path.count} height={path.count} fill="#fff" />
      <path d={path.d} fill="#000" />
    </svg>
  );
}

function Labels() {
  const [assets, setAssets] = useState<Taggable[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState('');

  useEffect(() => {
    api.get<{ categories: Category[] }>('/api/categories').then((r) => setCategories(r.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    setAssets(null);
    api
      .get<{ assets: Taggable[] }>(`/api/assets${categoryId ? `?categoryId=${categoryId}` : ''}`)
      .then((r) => setAssets(r.assets))
      .catch(() => setAssets([]));
  }, [categoryId]);

  const chosen = (assets ?? []).filter((asset) => picked.has(asset.id));

  function toggle(id: string) {
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      <Panel title="What the code says">
        <p className="sub">
          Each label carries a link to that thing's page in this register, so scanning it with any
          phone camera opens the record — no app, and nothing to type. The asset tag is printed
          underneath, because a label that only a scanner can read is useless when the scanner is
          the thing that has gone missing.
        </p>
      </Panel>

      <Toolbar>
        <Field label="Only this kind">
          <Select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            <option value="">Everything</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </Field>
        <span className="sub">{picked.size} chosen</span>
        <Button variant="ghost" onClick={() => setPicked(new Set((assets ?? []).map((a) => a.id)))}>
          Choose all
        </Button>
        <Button variant="ghost" disabled={picked.size === 0} onClick={() => setPicked(new Set())}>
          Clear
        </Button>
        <Button variant="primary" disabled={picked.size === 0} onClick={() => window.print()}>
          Print {picked.size || ''} {picked.size === 1 ? 'label' : 'labels'}
        </Button>
      </Toolbar>

      {!assets ? (
        <Spinner />
      ) : assets.length === 0 ? (
        <EmptyState title="Nothing to label" description="Add something to the register first." />
      ) : (
        <>
          {/* Hidden on screen, and the only thing on the page when printing. */}
          <div className="labels-sheet">
            {chosen.map((asset) => (
              <div key={asset.id} className="label">
                <QrSvg text={`${window.location.origin}/assets/${asset.id}`} />
                <div className="label__text">
                  <strong>{asset.assetTag ?? asset.name}</strong>
                  <span>{asset.assetTag ? asset.name : asset.categoryName}</span>
                  {asset.serialNumber && <span className="label__serial">{asset.serialNumber}</span>}
                </div>
              </div>
            ))}
          </div>

          <Card padded={false} className="labels-picker">
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th />
                    <th>Asset</th>
                    <th>Tag</th>
                    <th>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => (
                    <tr key={asset.id}>
                      <td>
                        {/* The name as the label, hidden visually: the row
                            beside it says what this is, but a screen reader
                            reading "checkbox, checkbox, checkbox" does not. */}
                        <Checkbox
                          className="checkbox-bare"
                          label={`Print a label for ${asset.name}`}
                          checked={picked.has(asset.id)}
                          onChange={() => toggle(asset.id)}
                        />
                      </td>
                      <td className="tcell-name">{asset.name}</td>
                      <td className="sub">{asset.assetTag ?? '—'}</td>
                      <td className="sub">{asset.categoryName}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </Card>
        </>
      )}
    </>
  );
}

/* --------------------------------------------------------- stock-take -- */

type Countable = {
  id: string;
  name: string;
  assetTag: string | null;
  serialNumber: string | null;
  state: string;
  categoryName: string;
  locationName: string | null;
  heldByName: string | null;
};

/**
 * Walking round with the register to see whether it is true.
 *
 * Nothing is marked lost unless it is asked for explicitly. An interrupted
 * stock-take -- somebody got to the second shelf and went to lunch -- would
 * otherwise turn into a register claiming half the office had vanished.
 */
function StockTake() {
  const [assets, setAssets] = useState<Countable[] | null>(null);
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState('');
  const [markLost, setMarkLost] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api.get<{ locations: Location[] }>('/api/locations').then((r) => setLocations(r.locations)).catch(() => {});
  }, []);

  const load = useCallback(async (where: string) => {
    setAssets(null);
    setSeen(new Set());
    const result = await api.get<{ assets: Countable[] }>(
      `/api/reports/stock-take${where ? `?locationId=${where}` : ''}`,
    );
    setAssets(result.assets);
  }, []);

  useEffect(() => {
    load(locationId).catch(() => setAssets([]));
  }, [locationId, load]);

  const missing = (assets ?? []).filter((asset) => !seen.has(asset.id));

  async function finish() {
    setBusy(true);
    try {
      const result = await api.post<{ markedLost: number }>('/api/reports/stock-take', {
        seen: [...seen],
        missing: missing.map((asset) => asset.id),
        markMissingLost: markLost,
      });
      toast.show(
        markLost
          ? `Done. ${result.markedLost} marked lost.`
          : `Done. ${missing.length} not found, nothing changed.`,
      );
      await load(locationId);
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not record it.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Toolbar>
        <Field label="Where you are looking">
          <Select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
            <option value="">Everywhere</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </Select>
        </Field>
        <span className="sub">
          {seen.size} of {assets?.length ?? 0} found
        </span>
      </Toolbar>

      {!assets ? (
        <Spinner />
      ) : assets.length === 0 ? (
        <EmptyState
          title="Nothing to count"
          description="Retired things are left out — they are kept for the record and are not on any shelf."
        />
      ) : (
        <>
          <Card padded={false}>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Found it</th>
                    <th>Asset</th>
                    <th>Where it should be</th>
                    <th>Who should have it</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => (
                    <tr key={asset.id} className={seen.has(asset.id) ? 'row-seen' : undefined}>
                      <td>
                        <Checkbox
                          className="checkbox-bare"
                          label={`Found ${asset.name}`}
                          checked={seen.has(asset.id)}
                          onChange={() =>
                            setSeen((was) => {
                              const next = new Set(was);
                              if (next.has(asset.id)) next.delete(asset.id);
                              else next.add(asset.id);
                              return next;
                            })
                          }
                        />
                      </td>
                      <td className="tcell-name">
                        {asset.name}
                        <div className="sub">{asset.assetTag ?? asset.serialNumber ?? '—'}</div>
                      </td>
                      <td className="sub">{asset.locationName ?? '—'}</td>
                      <td className="sub">
                        {asset.heldByName ?? <Badge tone="neutral">on the shelf</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </Card>

          <Panel title="When you have finished">
            <p className="sub">
              {missing.length === 0
                ? 'Everything was found. The register is true.'
                : `${missing.length} ${missing.length === 1 ? 'thing was' : 'things were'} not found.`}
            </p>

            {missing.length > 0 && (
              <label className="modal-checkbox" style={{ marginTop: 10 }}>
                <input type="checkbox" checked={markLost} onChange={(e) => setMarkLost(e.target.checked)} />
                Mark those {missing.length} as lost
              </label>
            )}

            <div className="row-actions" style={{ marginTop: 12 }}>
              <Button variant="primary" disabled={busy || seen.size === 0} onClick={() => void finish()}>
                {busy ? 'Recording…' : 'Record this stock-take'}
              </Button>
            </div>
          </Panel>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------- export -- */

const EXPORTS = [
  {
    path: '/api/reports/export/assets',
    label: 'Everything tracked one by one',
    blurb: 'One row per thing, with who has it, what it cost and when the warranty ends.',
  },
  {
    path: '/api/reports/export/stock',
    label: 'Stock levels',
    blurb: 'How many of each kind there are, how many are out, and how many are broken.',
  },
  {
    path: '/api/reports/export/people',
    label: 'Who has what',
    blurb: 'One row per thing somebody holds. The list finance usually asks for.',
  },
] as const;

function Export() {
  return (
    <>
      <Notice tone="accent" title="These are files, not pages">
        Each one downloads a CSV that opens in any spreadsheet — which is what actually happens to an
        asset register, so it is worth doing properly rather than leaving somebody to copy the table.
      </Notice>

      {EXPORTS.map((item) => (
        <Panel key={item.path} title={item.label}>
          <p className="sub">{item.blurb}</p>
          <div className="row-actions" style={{ marginTop: 10 }}>
            {/* A plain link: the cookie rides along and the browser does the
                saving, which a fetch would have to reimplement. */}
            <a className="btn btn-primary" href={item.path} download>
              Download
            </a>
          </div>
        </Panel>
      ))}
    </>
  );
}
